// EditPlanAnnaChat Component
// ===========================
// Chat interface for editing existing learning plans with Anna
// Feature #99 - Edit variant of JourneyAnnaChat for plan modifications
// Feature #101 - Uses EditPlanGeneratorService with edit-specific context

import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import { useTranslation } from 'react-i18next';
import {
  EditPlanGeneratorService,
  ChatMessage,
  SuggestedLearningPlan,
  EditPlanGenerationContext,
} from '../../services/annaTextChat';
import type { JourneyMaterial, LearningJourney, Topic } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../ui/Button';
// Feature #219: Removed AnnaAvatar import - Anna's personality comes from her words, not repeated images

// Helper function to extract first name from display name
const getFirstName = (displayName: string | null | undefined, defaultName: string): string => {
  if (!displayName) return defaultName;
  const firstName = displayName.split(' ')[0];
  return firstName || defaultName;
};

interface EditPlanAnnaChatProps {
  /** The learning journey this chat belongs to */
  journey: LearningJourney;
  /** Existing topics in the learning plan */
  existingTopics: Topic[];
  /** Optionally uploaded new materials */
  newMaterials?: JourneyMaterial[];
  /** IDs of materials already analyzed when plan was created (Feature #180) */
  analyzedMaterialIds?: string[];
  /** Callback when a new learning plan is approved */
  onPlanApproved: (plan: SuggestedLearningPlan) => void;
  /** Optional callback to close the chat */
  onClose?: () => void;
}

const EditPlanAnnaChat: React.FC<EditPlanAnnaChatProps> = ({
  journey,
  existingTopics,
  newMaterials = [],
  analyzedMaterialIds = [],
  onPlanApproved,
  onClose,
}) => {
  // Get user context for personalized greeting
  const { user } = useAuth();
  const { t } = useTranslation('library');

  // Chat service instance - Feature #101: using EditPlanGeneratorService
  const [chatService, setChatService] = useState<EditPlanGeneratorService | null>(null);

  // Messages in the conversation
  const [messages, setMessages] = useState<ChatMessage[]>([]);

  // Current input text
  const [inputText, setInputText] = useState('');

  // Loading states
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  // Error state
  const [error, setError] = useState<string | null>(null);

  // Feature #248: Track if user has chatted with Anna before allowing plan generation
  const [hasUserChatted, setHasUserChatted] = useState(false);

  // Currently displayed plan (most recent from messages)
  const [currentPlan, setCurrentPlan] = useState<SuggestedLearningPlan | null>(null);

  // Refs
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const isInitializedRef = useRef(false);

  // Generate personalized initial message for editing context
  const getInitialMessage = useCallback((): ChatMessage => {
    const firstName = getFirstName(user?.displayName, t('annaChat.defaultName'));
    // Feature #180: Only count materials that weren't analyzed when plan was created
    const filteredNewMaterials = newMaterials.filter(m => !analyzedMaterialIds.includes(m.id));
    const hasNewMaterials = filteredNewMaterials.length > 0;
    const topicCount = existingTopics.length;
    const subtopicCount = existingTopics.reduce((sum, tp) => sum + (tp.subtopics?.length || 0), 0);

    let content = t('annaChat.editPlan.welcomeIntro', { firstName, subject: journey.subject });

    content += '\n\n' + t('annaChat.editPlan.welcomePlanStats', { topicCount, subtopicCount });

    if (hasNewMaterials) {
      content += '\n\n' + t('annaChat.editPlan.welcomeNewMaterials', { count: filteredNewMaterials.length });
    }

    content += '\n\n' + t('annaChat.editPlan.welcomeActions');

    return {
      id: `welcome-edit-${Date.now()}`,
      role: 'assistant',
      content,
      timestamp: new Date(),
    };
  }, [user?.displayName, journey.subject, existingTopics, newMaterials, analyzedMaterialIds, t]);

  // Initialize chat service
  useEffect(() => {
    if (isInitializedRef.current) return;
    isInitializedRef.current = true;

    // Feature #101: Use EditPlanGenerationContext with existing topics and new materials
    const context: EditPlanGenerationContext = {
      journeyId: journey.id,
      journeySubject: journey.subject || journey.title || '',
      examDate: journey.examDate,
      examFormat: journey.examFormat,
      existingTopics: existingTopics.map(tp => ({
        id: tp.id,
        title: tp.title,
        subtopics: (tp.subtopics || []).map(s => ({
          id: s.id,
          title: s.title,
          concepts: s.concepts || [],
          isCompleted: s.isCompleted || s.completed || false,
        })),
        completedSessions: tp.completedSessions || 0,
        totalEstimatedSessions: tp.totalEstimatedSessions || 1,
      })),
      // Feature #101: Include new materials for integration
      newMaterials: newMaterials.map(m => ({
        fileName: m.fileName,
        extractedText: m.extractedText || '',
      })),
    };

    // Feature #101: Use EditPlanGeneratorService for editing existing plans
    const service = new EditPlanGeneratorService(context);
    setChatService(service);

    // Add personalized welcome message for edit context
    const initialMessage = getInitialMessage();
    setMessages([initialMessage]);

    inputRef.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (container) {
      // Use requestAnimationFrame to ensure DOM has updated
      requestAnimationFrame(() => {
        container.scrollTo({
          top: container.scrollHeight,
          behavior: 'smooth'
        });
      });
    }
  }, [messages]);

  // Update current plan when messages change
  useEffect(() => {
    // Find the most recent message with a plan (iterate backwards)
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].suggestedPlan) {
        setCurrentPlan(messages[i].suggestedPlan ?? null);
        return;
      }
    }
  }, [messages]);

  // Generate new learning plan from existing + new materials
  const handleGeneratePlan = useCallback(async () => {
    if (!chatService || isGenerating) return;

    // Build context from existing topics and new materials
    const existingTopicsText = existingTopics.map((tp, idx) => {
      const subtopics = (tp.subtopics || []).map(s => `  - ${s.title}`).join('\n');
      return `${idx + 1}. ${tp.title}\n${subtopics}`;
    }).join('\n\n');

    const newMaterialTexts = newMaterials
      .filter(m => m.extractedText)
      .map(m => `--- ${m.fileName} ---\n${m.extractedText}`);

    const combinedContext = [
      `EKSISTERENDE EMNEPLAN:\n${existingTopicsText}`,
      ...(newMaterialTexts.length > 0 ? [`\nNYT MATERIALE:\n${newMaterialTexts.join('\n\n')}`] : []),
    ];

    setIsGenerating(true);
    setError(null);

    // Add a user message indicating plan generation
    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: newMaterials.length > 0
        ? t('annaChat.editPlan.generateWithNewMaterial')
        : t('annaChat.editPlan.generateFromConversation'),
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, userMessage]);

    try {
      const response = await chatService.generatePlanFromMaterials(combinedContext);
      setMessages(prev => [...prev, response]);

      if (response.suggestedPlan) {
        setCurrentPlan(response.suggestedPlan);
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : t('annaChat.errors.unknownError');
      setError(errorMessage || t('annaChat.errors.couldNotGenerate'));
    } finally {
      setIsGenerating(false);
      inputRef.current?.focus();
    }
  }, [chatService, existingTopics, newMaterials, isGenerating, t]);

  // Send a message to refine the plan
  const handleSendMessage = async () => {
    if (!inputText.trim() || !chatService || isLoading) return;

    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: inputText.trim(),
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputText('');
    setIsLoading(true);
    setError(null);

    // Feature #248: Mark that user has chatted with Anna
    setHasUserChatted(true);

    try {
      const response = await chatService.sendMessage(userMessage.content);
      setMessages(prev => [...prev, response]);

      if (response.suggestedPlan) {
        setCurrentPlan(response.suggestedPlan);
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : t('annaChat.errors.unknownError');
      setError(errorMessage || t('annaChat.errors.couldNotSend'));
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  // Handle Enter key
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Approve the current plan
  const handleApprovePlan = () => {
    if (currentPlan) {
      onPlanApproved(currentPlan);
    }
  };

  // Format timestamp
  const formatTime = (date: Date) => {
    return new Intl.DateTimeFormat('da-DK', {
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  // Feature #216: Check if we have TRULY new materials with text (filter by analyzedMaterialIds)
  const trulyNewMaterials = newMaterials.filter(m => !analyzedMaterialIds.includes(m.id));
  const hasNewMaterialsWithText = trulyNewMaterials.some(m => m.extractedText);

  return (
    <div className="flex flex-col h-full bg-white dark:bg-gray-900 rounded-xl overflow-hidden">
      {/* Header - Feature #219: Removed AnnaAvatar - Anna's personality comes from her words */}
      <div className="flex items-center justify-between px-4 py-3 bg-amber-500 dark:bg-amber-600 text-white">
        <div className="flex items-center gap-3">
          <div>
            <h3 className="font-semibold">{t('annaChat.editPlan.headerTitle')}</h3>
            <p className="text-xs text-amber-100">{journey.subject}</p>
          </div>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            aria-label={t('annaChat.closeChat')}
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Existing Plan Context Bar */}
      <div className="px-4 py-2 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800">
        {/* Feature #216: Show only truly new materials (not already analyzed) */}
        <div className="flex items-center gap-2 text-sm text-amber-700 dark:text-amber-300">
          <span>📋</span>
          <span>
            {t('annaChat.editPlan.existingPlanTopics', { count: existingTopics.length })}
            {trulyNewMaterials.length > 0 && (
              <span className="ml-2 text-green-600 dark:text-green-400">
                {t('annaChat.editPlan.newMaterials', { count: trulyNewMaterials.length })}
              </span>
            )}
          </span>
        </div>
      </div>

      {/* Messages Container */}
      <div
        ref={messagesContainerRef}
        className="flex-1 overflow-y-auto p-4 space-y-4"
      >
        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                message.role === 'user'
                  ? 'bg-amber-500 text-white rounded-br-md'
                  : 'bg-gray-100 dark:bg-gray-800 text-neutral-dark dark:text-gray-200 rounded-bl-md'
              }`}
            >
              {/* Message content */}
              <div className="text-sm leading-relaxed prose prose-sm dark:prose-invert max-w-none prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0">
                <ReactMarkdown
                  components={{
                    ul: ({ children }) => (
                      <ul className="list-disc pl-4 my-1 space-y-0.5">{children}</ul>
                    ),
                    ol: ({ children }) => (
                      <ol className="list-decimal pl-4 my-1 space-y-0.5">{children}</ol>
                    ),
                    li: ({ children }) => <li className="my-0">{children}</li>,
                    p: ({ children }) => <p className="my-1">{children}</p>,
                    strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                    em: ({ children }) => <em className="italic">{children}</em>,
                  }}
                >
                  {message.content}
                </ReactMarkdown>
              </div>

              {/* Suggested Plan Preview */}
              {message.suggestedPlan && (
                <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                  <div className="bg-amber-50 dark:bg-amber-900/30 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-amber-800 dark:text-amber-300">
                        📋 {t('annaChat.editPlan.newPlanSuggested')}
                      </span>
                      <span className="text-xs text-amber-600 dark:text-amber-400">
                        {t('annaChat.estimatedHours', { hours: message.suggestedPlan.estimatedTotalHours })}
                      </span>
                    </div>
                    <p className="text-xs text-amber-700 dark:text-amber-400 mb-2">
                      {message.suggestedPlan.summary}
                    </p>
                    <div className="space-y-1">
                      {message.suggestedPlan.topics.map((topic, idx) => (
                        <div key={idx} className="text-xs text-amber-600 dark:text-amber-400">
                          • {topic.title}
                          {topic.subtopics && topic.subtopics.length > 0 && (
                            <span className="text-amber-400 dark:text-amber-500">
                              {' '}({t('annaChat.subtopicCount', { count: topic.subtopics.length })})
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Timestamp */}
              <div
                className={`text-xs mt-2 ${
                  message.role === 'user' ? 'text-amber-200' : 'text-gray-400 dark:text-gray-500'
                }`}
              >
                {formatTime(message.timestamp)}
              </div>
            </div>
          </div>
        ))}

        {/* Loading indicator */}
        {(isLoading || isGenerating) && (
          <div className="flex justify-start">
            <div className="bg-gray-100 dark:bg-gray-800 rounded-2xl rounded-bl-md px-4 py-3">
              <div className="flex items-center gap-2 text-gray-500">
                <div className="flex gap-1">
                  <div className="w-2 h-2 bg-amber-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <div className="w-2 h-2 bg-amber-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <div className="w-2 h-2 bg-amber-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
                <span className="text-sm">
                  {isGenerating ? t('annaChat.editPlan.updatingPlan') : t('annaChat.annaTyping')}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Error message */}
        {error && (
          <div className="flex justify-center">
            <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg px-4 py-2 text-sm">
              ⚠️ {error}
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Quick Actions */}
      <div className="px-4 py-2 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700">
        <div className="flex flex-wrap gap-2 items-center">
          {!currentPlan && (
            <>
              <Button
                variant="primary"
                size="sm"
                onClick={handleGeneratePlan}
                disabled={isGenerating || isLoading || !hasUserChatted}
                className="flex items-center gap-2 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed"
                title={!hasUserChatted ? t('annaChat.editPlan.chatFirstTitle') : undefined}
              >
                ✨ {t('annaChat.editPlan.generateRevisedPlan')}
              </Button>
              {/* Feature #248: Show message when user hasn't chatted yet */}
              {!hasUserChatted && (
                <span className="text-xs text-amber-600 dark:text-amber-400 italic">
                  💬 {t('annaChat.editPlan.chatFirstHint')}
                </span>
              )}
            </>
          )}
          {currentPlan && (
            <>
              <Button
                variant="primary"
                size="sm"
                onClick={handleApprovePlan}
                disabled={isLoading}
                className="flex items-center gap-2 bg-green-600 hover:bg-green-700"
              >
                ✅ {t('annaChat.editPlan.approveNewPlan')}
              </Button>
              <button
                onClick={handleGeneratePlan}
                disabled={isGenerating || isLoading}
                className="text-xs px-3 py-1.5 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-full hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors text-neutral-dark dark:text-gray-200 disabled:opacity-50"
              >
                🔄 {t('annaChat.editPlan.generateNewPlan')}
              </button>
              <button
                onClick={() => setInputText(t('annaChat.editPlan.keepExistingAddSubtopics'))}
                className="text-xs px-3 py-1.5 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-full hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors text-neutral-dark dark:text-gray-200"
              >
                📝 {t('annaChat.moreDetail')}
              </button>
            </>
          )}
          {hasNewMaterialsWithText && !currentPlan && hasUserChatted && (
            <span className="text-xs text-green-600 dark:text-green-400 py-1.5">
              ✓ {t('annaChat.editPlan.newMaterialReady')}
            </span>
          )}
        </div>
      </div>

      {/* Input Area */}
      <div className="p-4 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            type="text"
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('annaChat.editPlan.inputPlaceholder')}
            className="flex-1 px-4 py-2.5 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-full focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm text-neutral-dark dark:text-gray-200 placeholder:text-gray-400"
            disabled={isLoading || isGenerating}
          />
          <Button
            variant="primary"
            onClick={handleSendMessage}
            disabled={!inputText.trim() || isLoading || isGenerating}
            className="rounded-full px-4 bg-amber-500 hover:bg-amber-600"
            aria-label={t('annaChat.sendMessage')}
          >
            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
            </svg>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EditPlanAnnaChat;
